import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPassword, verifyResetToken } from '../services/auth';
import './ResetPasswordPage.css';
import BrandLogo from '../components/BrandLogo';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Kein Reset-Token gefunden');
        setVerifying(false);
        return;
      }

      try {
        await verifyResetToken(token);
        setTokenValid(true);
      } catch (err: any) {
        const message = err.response?.data?.error || 'Ungültiger oder abgelaufener Reset-Token';
        setError(message);
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    if (!token) {
      setError('Kein Reset-Token gefunden');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Fehler beim Zurücksetzen des Passworts';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <div className="container login-header-inner">
          <div className="login-logo">
            <Link to="/" aria-label="ELFHALBE — zur Startseite">
              <BrandLogo height={18} withIcon />
            </Link>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
            Zur Startseite
          </button>
        </div>
      </header>
      <div className="login-container">
        <div className="login-card">
          <h1>Neues Passwort setzen</h1>
          {verifying ? (
            <div className="loading">Token wird überprüft...</div>
          ) : !tokenValid ? (
            <div>
              <div className="error-message">
                {error || 'Ungültiger oder abgelaufener Reset-Token'}
              </div>
              <div style={{ marginTop: '20px' }}>
                <Link to="/forgot-password" className="btn btn-primary">
                  Neuen Reset-Link anfordern
                </Link>
              </div>
            </div>
          ) : success ? (
            <div>
              <div className="success-message">
                Passwort wurde erfolgreich zurückgesetzt. Du wirst zum Login weitergeleitet...
              </div>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Gib dein neues Passwort ein. Es muss mindestens 6 Zeichen lang sein.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="password">Neues Passwort</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Passwort bestätigen</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={6}
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}
                </button>
                <div className="register-link">
                  <Link to="/login">Zurück zum Login</Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
